"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import EnhancedUserAccount from "@/components/common/EnhancedUserAccount";
import { LuckyGiftClient } from "@/lib/clients/LuckyGiftClient";
import type { GiftDynamicMessage } from "../../hooks/useLuckyGiftDynamics";

interface GiftDynamicsScrollProps {
	dynamics: GiftDynamicMessage[];
	isLoading: boolean;
	language: "zh" | "en";
}

export default function GiftDynamicsScroll({
	dynamics,
	isLoading,
	language,
}: GiftDynamicsScrollProps) {
	const [isPaused, setIsPaused] = useState(false);
	const router = useRouter();

	const translations = {
		zh: {
			loading: "加载中...",
			noData: "静待福泽，暂无动态",
			claimed: "领取了",
			from: "来自",
		},
		en: {
			loading: "Loading...",
			noData: "No dynamics yet",
			claimed: "claimed",
			from: "from",
		},
	};

	const t = translations[language];

	// 智能金额格式化逻辑
	const formatSmartAmount = (amount: bigint, decimals: number): string => {
		const val = Number(amount) / 10 ** decimals;
		if (val > 1) {
			return val.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}
		if (val > 0.1) {
			return val.toLocaleString(undefined, {
				minimumFractionDigits: 3,
				maximumFractionDigits: 3,
			});
		}
		return LuckyGiftClient.formatAmount(amount, decimals);
	};

	const formatTime = (timestamp: bigint): string => {
		const ms = Number(timestamp) / 1_000_000;
		const date = new Date(ms);
		return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
	};

	const handleMessageClick = () => {
		router.push("/plaza/lucky-gift?tab=feed");
	};

	if (isLoading) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-center text-gray-400 dark:text-gray-500">
					<div className="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin mb-2" />
					<p className="text-sm">{t.loading}</p>
				</div>
			</div>
		);
	}

	if (dynamics.length === 0) {
		return (
			<div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-600">
				<div className="text-4xl mb-2 opacity-50">🧧</div>
				<p className="text-sm font-medium">{t.noData}</p>
			</div>
		);
	}

	const renderItem = (msg: GiftDynamicMessage, keyPrefix: string) => {
		const formattedAmount = formatSmartAmount(
			msg.amount,
			msg.tokenInfo.displayToChainDecimals,
		);

		return (
			<div
				key={`${keyPrefix}-${msg.id}`}
				onClick={handleMessageClick}
				className="p-2.5 rounded-xl cursor-pointer transition-all duration-300 bg-white/40 dark:bg-white/5 border border-transparent hover:border-red-100 dark:hover:border-red-900/30 hover:bg-white/80 dark:hover:bg-white/10 group"
			>
				<div className="flex items-start gap-2">
					<div className="flex-shrink-0 mt-0.5">
						<Image
							src={`https://images.odin.fun/token/${msg.tokenInfo.tokenId}`}
							alt={msg.tokenInfo.tokenId}
							width={20}
							height={20}
							className="rounded-full grayscale-[30%] group-hover:grayscale-0 transition-all opacity-80 group-hover:opacity-100"
						/>
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-1.5 mb-0.5">
							<span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
								[{formatTime(msg.timestamp)}]
							</span>
							<span className="text-xs font-bold text-light-text dark:text-dark-text truncate">
								<EnhancedUserAccount
									userid={msg.claimer}
									showCopy={false}
									language={language}
									enableUserInfo={true}
									fallbackFormat="short"
								/>
							</span>
						</div>
						<p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
							{t.claimed}{" "}
							<span className="font-bold text-red-600 dark:text-red-400">
								{formattedAmount} {msg.tokenInfo.displayUnitName}
							</span>{" "}
							{t.from}{" "}
							<span className="underline decoration-gray-200 dark:decoration-gray-800">
								{msg.sender.slice(0, 5)}...
							</span>
						</p>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 relative overflow-hidden">
				<div
					className={`absolute inset-0 space-y-2 ${isPaused ? "" : "animate-gift-scroll-up"}`}
					onMouseEnter={() => setIsPaused(true)}
					onMouseLeave={() => setIsPaused(false)}
				>
					{dynamics.map((msg) => renderItem(msg, "first"))}
					{dynamics.map((msg) => renderItem(msg, "second"))}
				</div>
			</div>

			<style jsx>{`
        @keyframes giftScrollUp {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-gift-scroll-up {
          animation: giftScrollUp 30s linear infinite;
        }
      `}</style>
		</div>
	);
}
